import type {
  CalendarQuery,
  CalendarResponse,
  ChartQuery,
  ChartResponse,
  CoverageQuery,
  CoverageResponse,
  CreateInstrumentBody,
  EnqueueJobBody,
  HealthResponse,
  IngestPlan,
  InstrumentsQuery,
  InstrumentsResponse,
  Job,
  JobKindInfo,
  JobListResponse,
  JobLogLine,
  ListJobsQuery,
  PatchInstrumentBody,
  PlanIngestBody,
  PublicUser,
  SetupStatus,
  CredentialSourceInfo,
  CredentialView,
  ServiceState,
  ServicesResponse,
  SystemStatus,
  CreateLabelSession,
  CreateSkip,
  CreateTradeLabel,
  LabelListResponse,
  LabelQueue,
  LabelQueueDefinition,
  LabelSession,
  LabelStats,
  ListLabelsQuery,
  TradeLabel,
  CloseReplayPosition,
  OpenReplayPosition,
  ReplayChart,
  ReplayQueue,
  StartReplaySessionInput,
  LabelAgreement,
  LabelAnalysisQuery,
  LabelDrift,
  LabelQc,
  LabelStatistics,
} from '@gitrader/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    headers: {
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...init.headers,
    },
    ...init,
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const err = (payload as { error?: { code?: string; message?: string; details?: unknown } })
      ?.error;
    throw new ApiError(
      response.status,
      err?.code ?? 'request_failed',
      err?.message ?? `Request failed with ${response.status}`,
      err?.details,
    );
  }

  return payload as T;
}

export const api = {
  setupStatus: () => request<SetupStatus>('/api/v1/setup/status'),

  bootstrap: (body: {
    bootstrapKey: string;
    email: string;
    displayName: string;
    password: string;
  }) =>
    request<{ user: PublicUser }>('/api/v1/setup/bootstrap', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  login: (body: { email: string; password: string }) =>
    request<{ user: PublicUser }>('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  logout: () => request<void>('/api/v1/auth/logout', { method: 'POST' }),

  me: () => request<{ user: PublicUser }>('/api/v1/auth/me'),

  health: () => request<HealthResponse>('/api/v1/health'),

  /** Read-only views over A2's tables (Quality and Calendar). */
  coverage: (q: Partial<CoverageQuery>) =>
    request<CoverageResponse>(`/api/v1/data/coverage${queryString(q)}`),
  calendar: (q: CalendarQuery) =>
    request<CalendarResponse>(`/api/v1/data/calendar${queryString(q)}`),
  /** Bars, indicator rows, daily context and coverage for one session range, in one request. */
  chart: (q: Partial<ChartQuery>) =>
    request<ChartResponse>(`/api/v1/data/chart${queryString(stringify(q))}`),

  /** The operations console (A5). Every call here is superadmin-only on the server. */
  jobKinds: () =>
    request<{ items: JobKindInfo[]; credentialSource: string; credentials?: CredentialSourceInfo }>(
      '/api/v1/jobs/kinds',
    ),
  enqueueJob: (body: EnqueueJobBody) =>
    request<Job>('/api/v1/jobs', { method: 'POST', body: JSON.stringify(body) }),
  jobs: (q: ListJobsQuery = {}) =>
    request<JobListResponse>(`/api/v1/jobs${queryString(stringify(q))}`),
  job: (id: string) => request<Job>(`/api/v1/jobs/${encodeURIComponent(id)}`),
  jobLog: (id: string, after = 0, limit = 2000) =>
    request<{ jobId: string; status: Job['status']; lines: JobLogLine[] }>(
      `/api/v1/jobs/${encodeURIComponent(id)}/log?after=${after}&limit=${limit}`,
    ),
  cancelJob: (id: string) =>
    request<Job>(`/api/v1/jobs/${encodeURIComponent(id)}/cancel`, { method: 'POST' }),
  /** The same-origin URL the browser's EventSource opens; the session cookie authenticates it. */
  jobStreamUrl: (id: string) => `/api/v1/jobs/${encodeURIComponent(id)}/stream`,
  ingestPlan: (body: PlanIngestBody) =>
    request<IngestPlan>('/api/v1/ingest/plan', { method: 'POST', body: JSON.stringify(body) }),
  instruments: (q: InstrumentsQuery = {}) =>
    request<InstrumentsResponse>(`/api/v1/instruments${queryString(stringify(q))}`),
  createInstrument: (body: CreateInstrumentBody) =>
    request<{ symbol: string; role: string; enabled: boolean; notes: string | null }>(
      '/api/v1/instruments',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  patchInstrument: (symbol: string, body: PatchInstrumentBody) =>
    request<{ symbol: string; role: string; enabled: boolean; notes: string | null }>(
      `/api/v1/instruments/${encodeURIComponent(symbol)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  systemStatus: () => request<SystemStatus>('/api/v1/system/status'),

  /**
   * Analysis → Labeling (B2/B3).
   *
   * No `labelerId` anywhere: it comes from the session cookie on the server. A client that
   * could assert who labelled something could corrupt one of the variables this dataset is
   * measuring, so the field simply does not exist on this surface.
   */
  createLabelSession: (body: CreateLabelSession) =>
    request<LabelSession>('/api/v1/label-sessions', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  createLabel: (body: CreateTradeLabel) =>
    request<TradeLabel>('/api/v1/labels', { method: 'POST', body: JSON.stringify(body) }),
  labels: (q: Partial<ListLabelsQuery> = {}) =>
    request<LabelListResponse>(`/api/v1/labels${queryString(stringify(q))}`),
  /** Correct a label: the body is a complete replacement, never a patch. */
  supersedeLabel: (id: string, reason: string, replacement: CreateTradeLabel) =>
    request<{ superseded: TradeLabel; replacement: TradeLabel }>(
      `/api/v1/labels/${encodeURIComponent(id)}/supersede`,
      { method: 'POST', body: JSON.stringify({ reason, replacement }) },
    ),
  /** Withdraw a label. Behaves like delete in the list; stays in the table, dated. */
  retractLabel: (id: string, reason: string) =>
    request<TradeLabel>(`/api/v1/labels/${encodeURIComponent(id)}/retract`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  labelQueue: () => request<LabelQueue>('/api/v1/labels/queue'),
  writeLabelQueue: (body: LabelQueueDefinition) =>
    request<LabelQueue>('/api/v1/labels/queue', { method: 'PUT', body: JSON.stringify(body) }),
  skipSession: (body: CreateSkip) =>
    request<{ ok: true }>('/api/v1/label-skips', { method: 'POST', body: JSON.stringify(body) }),
  clearSkip: (symbol: string, sessionDate: string) =>
    request<{ ok: true }>(`/api/v1/label-skips${queryString({ symbol, sessionDate })}`, {
      method: 'DELETE',
    }),
  labelStats: () => request<LabelStats>('/api/v1/labels/stats'),

  /**
   * Replay labelling (B4).
   *
   * Note what is NOT here: no `until`, no cursor, no "give me bars up to". The cursor is the
   * server's and the client never names it — a client that could say what it wanted to see
   * could ask for 16:00, and the labels that came out would look exactly like honest ones.
   * `advance` says "next", not "go to".
   */
  startReplay: (body: StartReplaySessionInput) =>
    request<ReplayChart>('/api/v1/replay/sessions', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  replayChart: (id: string) =>
    request<ReplayChart>(`/api/v1/replay/sessions/${encodeURIComponent(id)}/chart`),
  advanceReplay: (id: string, bars = 1) =>
    request<ReplayChart>(`/api/v1/replay/sessions/${encodeURIComponent(id)}/advance`, {
      method: 'POST',
      body: JSON.stringify({ bars }),
    }),
  openReplayPosition: (id: string, body: OpenReplayPosition) =>
    request<ReplayChart>(`/api/v1/replay/sessions/${encodeURIComponent(id)}/position`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  closeReplayPosition: (id: string, body: CloseReplayPosition) =>
    request<ReplayChart>(`/api/v1/replay/sessions/${encodeURIComponent(id)}/close`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  replayQueue: (symbol?: string) =>
    request<ReplayQueue>(`/api/v1/replay/queue${queryString({ symbol })}`),

  /**
   * Analysis → Label quality (B5). Four reads rather than one: they answer four different
   * questions and the agreement analysis is the expensive one, so a glance at a count by tag
   * should not recompute it.
   */
  labelStatistics: (q: Partial<LabelAnalysisQuery> = {}) =>
    request<LabelStatistics>(`/api/v1/labels/analysis/statistics${queryString(stringify(q))}`),
  labelQc: (q: Partial<LabelAnalysisQuery> = {}) =>
    request<LabelQc>(`/api/v1/labels/analysis/qc${queryString(stringify(q))}`),
  labelAgreement: (q: Partial<LabelAnalysisQuery> = {}) =>
    request<LabelAgreement>(`/api/v1/labels/analysis/agreement${queryString(stringify(q))}`),
  labelDrift: (q: Partial<LabelAnalysisQuery> = {}) =>
    request<LabelDrift>(`/api/v1/labels/analysis/drift${queryString(stringify(q))}`),
  labelExports: () =>
    request<{ dir: string; items: { name: string; bytes: number; modifiedAt: string }[] }>(
      '/api/v1/labels/exports',
    ),
  labelExportUrl: (name: string) => `/api/v1/labels/exports/${encodeURIComponent(name)}`,

  /** Admin → Connections (A6). Secrets go up; nothing but hints ever comes back. */
  services: () => request<ServicesResponse>('/api/v1/services'),
  writeCredential: (key: string, fields: Record<string, unknown>) =>
    request<{ created: CredentialView; revoked: CredentialView | null; service: ServiceState }>(
      `/api/v1/services/${encodeURIComponent(key)}/credential`,
      { method: 'POST', body: JSON.stringify(fields) },
    ),
  verifyCredential: (key: string) =>
    request<{ jobId: string; status: string }>(
      `/api/v1/services/${encodeURIComponent(key)}/verify`,
      {
        method: 'POST',
        body: '{}',
      },
    ),
  revokeCredential: (key: string) =>
    request<{ revoked: CredentialView; service: ServiceState }>(
      `/api/v1/services/${encodeURIComponent(key)}/revoke`,
      { method: 'POST', body: '{}' },
    ),
};

function stringify(o: Record<string, unknown>): Record<string, string | undefined> {
  return Object.fromEntries(
    Object.entries(o).map(([k, v]) => [k, v === undefined || v === null ? undefined : String(v)]),
  );
}

function queryString(params: Record<string, string | undefined>): string {
  const entries = Object.entries(params).filter((e): e is [string, string] => e[1] !== undefined);
  return entries.length ? `?${new URLSearchParams(entries).toString()}` : '';
}
